import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MapPin, Clock, Briefcase } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader } from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { JOBS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CareersPage() {
  const [selectedJob, setSelectedJob] = useState(JOBS[0].title);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    position: JOBS[0].title,
    experience_years: "",
    location: "",
    cover_letter: "",
    resume_url: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please complete name, email, and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/careers/apply`, form);
      toast.success("Application received. Our HR team will reach out shortly.");
      setForm({
        name: "",
        email: "",
        phone: "",
        position: selectedJob,
        experience_years: "",
        location: "",
        cover_letter: "",
        resume_url: "",
      });
    } catch {
      toast.error("Could not submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="careers-page">
      <Seo title="Careers — IDEAL ENTERPRISES" description="Join a fast-growing team building India's gas distribution infrastructure." />
      <PageHeader
        overline="CAREERS"
        title="Build a career on the right side of India's energy transition."
        subtitle="We hire engineers, supervisors, and HSE professionals who care about the craft of construction."
        bgImage="https://images.pexels.com/photos/6082416/pexels-photo-6082416.jpeg"
      />

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="overline">Open Positions</p>
            <h2 className="font-heading font-bold text-3xl text-brand-blue mt-3">{JOBS.length} active roles</h2>
            <div className="mt-8 space-y-4">
              {JOBS.map((j, i) => (
                <motion.button
                  key={j.id}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  onClick={() => {
                    setSelectedJob(j.title);
                    setForm((f) => ({ ...f, position: j.title }));
                    document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  data-testid={`job-card-${j.id}`}
                  className={`block w-full text-left border p-6 transition-all hover:border-brand-orange ${
                    selectedJob === j.title ? "border-brand-orange bg-orange-50/50" : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="flex justify-between items-start gap-4">
                    <h3 className="font-heading font-bold text-lg text-brand-blue">{j.title}</h3>
                    <span className="font-mono-tech text-[10px] tracking-wider text-brand-orange bg-orange-100 px-2 py-1">
                      APPLY
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
                    <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-brand-orange" />{j.location}</span>
                    <span className="inline-flex items-center gap-1.5"><Clock size={14} className="text-brand-orange" />{j.type}</span>
                    <span className="inline-flex items-center gap-1.5"><Briefcase size={14} className="text-brand-orange" />{j.experience}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-4 leading-relaxed">{j.description}</p>
                </motion.button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div id="apply-form" className="bg-brand-blue text-white p-8 sticky top-28">
              <p className="overline text-brand-orange">Apply Now</p>
              <h3 className="font-heading font-bold text-2xl mt-3">Applying for: {selectedJob}</h3>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label className="text-white/80 text-xs uppercase tracking-wider">Position</Label>
                  <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="careers-position" readOnly className="bg-white/5 border-white/20 rounded-none text-white placeholder:text-white/40 mt-1.5" />
                </div>
                <div>
                  <Label className="text-white/80 text-xs uppercase tracking-wider">Full Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="careers-name" className="bg-white/5 border-white/20 rounded-none text-white placeholder:text-white/40 mt-1.5" />
                </div>
                <div>
                  <Label className="text-white/80 text-xs uppercase tracking-wider">Email *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="careers-email" className="bg-white/5 border-white/20 rounded-none text-white placeholder:text-white/40 mt-1.5" />
                </div>
                <div>
                  <Label className="text-white/80 text-xs uppercase tracking-wider">Phone *</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required data-testid="careers-phone" className="bg-white/5 border-white/20 rounded-none text-white placeholder:text-white/40 mt-1.5" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-white/80 text-xs uppercase tracking-wider">Experience</Label>
                    <Input value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} placeholder="5 years" data-testid="careers-experience" className="bg-white/5 border-white/20 rounded-none text-white placeholder:text-white/40 mt-1.5" />
                  </div>
                  <div>
                    <Label className="text-white/80 text-xs uppercase tracking-wider">Location</Label>
                    <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City" data-testid="careers-location" className="bg-white/5 border-white/20 rounded-none text-white placeholder:text-white/40 mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label className="text-white/80 text-xs uppercase tracking-wider">Resume URL</Label>
                  <Input value={form.resume_url} onChange={(e) => setForm({ ...form, resume_url: e.target.value })} placeholder="Drive / Dropbox link" data-testid="careers-resume" className="bg-white/5 border-white/20 rounded-none text-white placeholder:text-white/40 mt-1.5" />
                </div>
                <div>
                  <Label className="text-white/80 text-xs uppercase tracking-wider">Cover Letter</Label>
                  <Textarea rows={3} value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} data-testid="careers-cover" className="bg-white/5 border-white/20 rounded-none text-white placeholder:text-white/40 mt-1.5" />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="careers-submit"
                  className="w-full bg-brand-orange text-white py-3.5 font-semibold btn-lift hover:bg-orange-600 disabled:opacity-60"
                >
                  {submitting ? "Submitting…" : "Submit Application"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
